import type { NodeId } from '../../lib/types';

/**
 * Where the dragged subtree actually is, while the committed positions say otherwise.
 *
 * A drag deliberately does NOT write the store on every frame — that is the whole point of
 * the ref-transform path — so between `pointerdown` and `pointerup` the position map is
 * stale by design for the nodes being dragged. This module is where the truth lives in the
 * meantime, and everything that has to draw during a drag (the wires, hit-testing) reads it.
 *
 * Stated out loud rather than hidden because "the store is stale" is exactly the kind of
 * thing that has to be deliberate and visible. It is null except during a drag.
 */

export interface DragOffset {
  ids: ReadonlySet<NodeId>;
  dx: number;
  dy: number;
}

let current: DragOffset | null = null;

export function setDragOffset(next: DragOffset | null): void {
  current = next;
}

export function getDragOffset(): DragOffset | null {
  return current;
}

/** How far a node is currently displaced from its committed position. Zero for most nodes. */
export function offsetOf(id: NodeId): { dx: number; dy: number } | null {
  if (!current || !current.ids.has(id)) return null;
  return { dx: current.dx, dy: current.dy };
}
